import isEqual from "lodash-es/isEqual";
import {
	TGetFranchiseUpdatedProps,
	IArea,
	IPayloadArea,
	TErrors,
	TPath,
	TFranchiseSelected,
	IFranchise,
	IMetaArea,
	IOemMetaData,
	TLocationDealerCodeState,
	ILocationDealerCode,
} from "@/Settings/types/administrationTools/locationDealerCodes";

export const getInitialArea = (label: string) => ({ areaLabel: label, areaValue: "", areaKey: "", areaShort: "", version: "" });

export const updateFranchiseArea = (newArea: IPayloadArea, area?: IArea): IArea => {
	if (area?.areaLabel === newArea.areaLabel) {
		const updatedArea: IArea = {
			areaLabel: newArea.areaLabel,
			areaValue: newArea.areaValue,
			areaKey: newArea.areaKey,
			areaShort: newArea.areaShort,
			version: newArea.version,
		};
		if (newArea.areas.length) {
			updatedArea["area"] = getInitialArea(newArea.areas[0].areaLabel);
		}
		return updatedArea;
	}
	return {
		area: updateFranchiseArea(newArea, area?.area),
		areaLabel: area?.areaLabel ?? "",
		areaValue: area?.areaValue ?? "",
		areaKey: area?.areaKey ?? "",
		areaShort: area?.areaShort ?? "",
		version: area?.version ?? "",
	};
};

export const findError = (errors: TErrors, path: TPath) => {
	const uniqueDealerCodeFS = errors?.details?.find((item) => item.type === "unique.DealerCodeFS");
	if (uniqueDealerCodeFS && path[1] === "dealerCodeFS") {
		return uniqueDealerCodeFS?.context?.details?.find(
			(item: { path: (string | number)[] }) => JSON.stringify(item.path) === JSON.stringify(path)
		);
	}

	return errors?.details?.find((item) => JSON.stringify(item?.path) === JSON.stringify(path));
};

export const getFranchiseUpdated = ({ franchise, selected, oemMetaData }: TGetFranchiseUpdatedProps) => {
	const groupToCompare = franchise.groups.length ? franchise.groups : [{ franchiseName: franchise.franchise }];
	if (isEqual(selected, groupToCompare)) {
		return null;
	}
	const isCombined = selected.length > 1;
	const groups = isCombined ? selected : [];
	const dealerCode = selected.length ? franchise.dealerCode : "";
	const franchiseName = getFranchiseName(selected, franchise, isCombined);
	const franchiseMetaData = oemMetaData?.franchises.find((entry) => franchiseName && entry.franchise.includes(franchiseName));
	const metaAreas = JSON.parse(franchiseMetaData?.areas ?? "[]");
	const area = getArea(franchise, metaAreas, selected);
	return {
		isCombined,
		area,
		groups,
		dealerCode,
		franchise: franchiseName,
	};
};

const getFranchiseName = (selected: TFranchiseSelected[], franchise: IFranchise, isCombined: boolean) => {
	if (!isCombined) {
		return selected[0]?.franchiseName || "";
	}
	return franchise.franchise || selected[0]?.franchiseName || "";
};

const getArea = (franchise: IFranchise, metaAreas: IMetaArea[], selected: TFranchiseSelected[]) => {
	const oldFranchise = franchise.groups?.[0]?.franchiseName ?? franchise.franchise;
	const prev = franchise.area ?? getInitialArea(metaAreas[0]?.areaLabel);
	return selected.length && selected.some((item) => item.franchiseName === oldFranchise) ? prev : getInitialArea(metaAreas[0]?.areaLabel);
};

export const getFranchiseProps = (franchise: IFranchise, oemMetaData?: IOemMetaData) => {
	const franchisesNames = oemMetaData?.franchisesNames.map((item) => ({ franchiseName: item })) ?? [{ franchiseName: "" }];
	const franchiseName = franchise?.isCombined ? franchise?.groups[0]?.franchiseName : franchise?.franchise;
	const franchiseMetaData = oemMetaData?.franchises.find((entry) => franchiseName && entry.franchise.includes(franchiseName));
	const metaAreas = JSON.parse(franchiseMetaData?.areas ?? "[]");
	const singleFranchiseSelected = franchise?.franchise ? [{ franchiseName: franchise?.franchise }] : [];
	return { franchisesNames, metaAreas, singleFranchiseSelected };
};

export const replaceStatesWithWarnings = (
	originalState: TLocationDealerCodeState,
	updatedState: TLocationDealerCodeState,
	warningPaths: number[][]
) => {
	return updatedState.map((oem, oemIndex) => {
		return {
			...oem,
			franchises: oem.franchises?.map((franchise, franchiseIndex) => {
				const isWarning = warningPaths.some((path) => path[0] === oemIndex && path[1] === franchiseIndex);
				if (isWarning) {
					const originalFranchise = originalState[oemIndex]?.franchises?.[franchiseIndex];
					return {
						...franchise,
						franchise: originalFranchise?.franchise ?? "",
						dealerCode: originalFranchise?.dealerCode ?? "",
						area: originalFranchise?.area,
					};
				}
				return franchise;
			}),
		};
	});
};

export const findDuplicateIndices = (dealerCodesFs: string[]) => {
	const seen: Record<string, number[]> = {}; // Store occurrences of dealerCodeFS
	const errorDetails: { message: string; path: (string | number)[] }[] = [];

	// Populate the `seen` object with indices of each dealerCodeFS
	dealerCodesFs.forEach((code, index) => {
		if (seen[code]) {
			seen[code].push(index);
		} else {
			seen[code] = [index];
		}
	});

	// Identify duplicates and add error details for each duplicate
	for (const [code, indices] of Object.entries(seen)) {
		if (indices.length > 1) {
			indices.forEach((index) => {
				errorDetails.push({
					message: `dealerCodeFS value '${code}' is duplicated`,
					path: [index, "dealerCodeFS"], // Manually setting the path
				});
			});
		}
	}
	return errorDetails;
};

export const getOemFranchiseDisabled = (oem?: IOemMetaData) => {
	return (
		oem?.franchisesNames.filter((item) => {
			const metaData = oem?.franchises.find((entry) => item && entry.franchise.includes(item));
			const areas = JSON.parse(metaData?.areas ?? "[]");
			return areas.length === 0;
		}) ?? []
	);
};

export const getOemFranchisesSelected = (oem: ILocationDealerCode) => {
	return [
		...(oem.franchises?.filter((franchise) => !franchise.isCombined).map((franchise) => franchise.franchise) ?? []),
		...(oem.franchises?.flatMap((franchise) => franchise.groups.map((group) => group.franchiseName)) ?? []),
	];
};
